import React from "react";
import { useFocusTrap } from "Hooks";
import PropTypes from "prop-types";

/**
 * A component that creates a focus trap, restricting keyboard navigation
 * within the component itself. This component is designed
 * for use in class components since `useFocusTrap` hook cannot
 * be used directly within class components.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.isActive - Activates the focus trap when true.
 *                                   When false, focus can move freely outside the component.
 * @param {boolean} [props.shouldDefaultFocusOnFirstChild=true] - If true,
 *                                   the focus will default to the first child
 *                                   element when the focus trap is activated.
 * @param {boolean} [props.shouldStopPropagation=true] - If true, keyboard events
 *                                   will not propagate outside of the focus trap.
 * @param {number} [props.delayInMsForFirstFocus=0] - Delay in milliseconds
 *                                   before focus is set on the first child element.
 * @param {Object} [props.defaultFocusRef=null] - A ref object pointing to the element
 *                                   that should receive focus by default. If provided,
 *                                   this element will be focused instead of the first focusable element.
 * @param {React.ReactNode} props.children - The content to be rendered within the focus trap.
 *
 * @returns {React.ReactNode} The rendered section with the focus trap applied.
 *
 * @example
 * // Example of using FocusTrapWrapper in a class component
 * class MyModal extends React.Component {
 *   constructor(props) {
 *     super(props);
 *     this.state = { isFocusTrapActive: true };
 *   }
 *
 *   render() {
 *     return (
 *       <FocusTrapWrapper isActive={this.state.isFocusTrapActive}>
 *         <h1>My Modal</h1>
 *         <button>Action 1</button>
 *         <button>Action 2</button>
 *         <button>Close</button>
 *       </FocusTrapWrapper>
 *     );
 *   }
 * }
 *
 * @see {@link src/AppComponents/LiveRemarkBox/LiveRemarkBox.js} for an example
 * of integrating this component in your class component.
 */
const FocusTrapWrapper = ({
  isActive,
  shouldDefaultFocusOnFirstChild = true,
  shouldStopPropagation = true,
  delayInMsForFirstFocus = 0,
  defaultFocusRef = null,
  children,
}) => {
  const focusTrapRef = useFocusTrap({
    isActive,
    shouldDefaultFocusOnFirstChild,
    shouldStopPropagation,
    delayInMsForFirstFocus,
    defaultFocusRef,
  });

  return <section ref={focusTrapRef}>{children}</section>;
};

FocusTrapWrapper.propTypes = {
  isActive: PropTypes.bool.isRequired,
  shouldDefaultFocusOnFirstChild: PropTypes.bool,
  shouldStopPropagation: PropTypes.bool,
  delayInMsForFirstFocus: PropTypes.number,
  defaultFocusRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  children: PropTypes.node.isRequired,
};

export default FocusTrapWrapper;

FocusTrapWrapper.displayName = "FocusTrapWrapper";
